import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Bold, Italic, List, ListOrdered, Heading1, Heading2, Link as LinkIcon, Undo2, Redo2 } from "lucide-react";

export default function RichTextEditor({ value, onChange, testid = "rte" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noreferrer", target: "_blank" } }),
    ],
    content: value || "",
    onUpdate: ({ editor: e }) => {
      onChange?.(e.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-invert max-w-none min-h-[14rem] focus:outline-none [&_a]:text-primary [&_h1]:font-display [&_h2]:font-display p-4",
        "data-testid": `${testid}-content`,
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (typeof value === "string" && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const Btn = ({ on, active, icon: Icon, label, testid: tid }) => (
    <button
      type="button"
      onClick={on}
      aria-label={label}
      title={label}
      data-testid={tid}
      className={`p-1.5 rounded-md hover:bg-secondary ${active ? "text-primary bg-secondary" : "text-muted-foreground"}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="border border-border rounded-md bg-background" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-border bg-card/40">
        <Btn
          on={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive("heading", { level: 1 })}
          icon={Heading1}
          label="Heading 1"
          testid={`${testid}-h1`}
        />
        <Btn
          on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
          icon={Heading2}
          label="Heading 2"
          testid={`${testid}-h2`}
        />
        <span className="w-px h-5 bg-border mx-1" />
        <Btn
          on={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          icon={Bold}
          label="Bold"
          testid={`${testid}-bold`}
        />
        <Btn
          on={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          icon={Italic}
          label="Italic"
          testid={`${testid}-italic`}
        />
        <span className="w-px h-5 bg-border mx-1" />
        <Btn
          on={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          icon={List}
          label="Bullet list"
          testid={`${testid}-bullet`}
        />
        <Btn
          on={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          icon={ListOrdered}
          label="Numbered list"
          testid={`${testid}-ordered`}
        />
        <span className="w-px h-5 bg-border mx-1" />
        <Btn
          on={() => {
            const url = window.prompt("Link URL", editor.getAttributes("link").href || "https://");
            if (url === null) return;
            if (url === "") editor.chain().focus().unsetLink().run();
            else editor.chain().focus().setLink({ href: url }).run();
          }}
          active={editor.isActive("link")}
          icon={LinkIcon}
          label="Link"
          testid={`${testid}-link`}
        />
        <span className="w-px h-5 bg-border mx-1" />
        <Btn on={() => editor.chain().focus().undo().run()} active={false} icon={Undo2} label="Undo" testid={`${testid}-undo`} />
        <Btn on={() => editor.chain().focus().redo().run()} active={false} icon={Redo2} label="Redo" testid={`${testid}-redo`} />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
